/**
 * Text utility functions for handling HTML entities and formatting
 */

/**
 * Decodes HTML entities in text, particularly useful for RSS feed content
 * that may contain encoded apostrophes, quotes, and other special characters
 * 
 * @param {string} text - The text containing HTML entities
 * @returns {string} - The decoded text
 */
export const decodeHtmlEntities = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Create a temporary DOM element to decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  let decoded = textarea.value;

  // Additional manual replacements for common entities that might not be caught
  const entityMap = {
    '&#39;': "'",
    '&apos;': "'",
    '&#x27;': "'",
    '&quot;': '"',
    '&#34;': '"',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&#8217;': "'", // Right single quotation mark
    '&#8216;': "'", // Left single quotation mark
    '&#8220;': '"', // Left double quotation mark
    '&#8221;': '"', // Right double quotation mark
    '&#8211;': '–', // En dash
    '&#8212;': '—', // Em dash
    '&#8230;': '…', // Horizontal ellipsis
  };

  // Replace each entity with its corresponding character
  Object.entries(entityMap).forEach(([entity, character]) => {
    decoded = decoded.replace(new RegExp(entity, 'g'), character);
  });

  return decoded;
};

/**
 * Removes HTML tags from text content
 * 
 * @param {string} text - The text that may contain HTML tags
 * @returns {string} - The text with HTML tags removed
 */
export const stripHtmlTags = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Remove HTML tags using regex
  let stripped = text.replace(/<[^>]*>/g, ' ');
  
  // Remove HTML comments
  stripped = stripped.replace(/<!--[\s\S]*?-->/g, '');
  
  // Normalize whitespace after tag removal
  stripped = stripped.replace(/\s+/g, ' ').trim();
  
  return stripped;
};

/**
 * Cleans and formats text for display, handling common RSS feed formatting issues
 * 
 * @param {string} text - The text to clean and format
 * @returns {string} - The cleaned and formatted text
 */
export const cleanText = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // First strip any HTML tags that might have slipped through
  let cleaned = stripHtmlTags(text);
  
  // Then decode HTML entities
  cleaned = decodeHtmlEntities(cleaned);
  
  // Remove excessive whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  // Fix common punctuation spacing issues
  cleaned = cleaned.replace(/\s+([,.!?;:])/g, '$1'); // Remove space before punctuation
  cleaned = cleaned.replace(/([.!?])\s*([A-Z])/g, '$1 $2'); // Ensure space after sentence endings
  
  return cleaned;
};

/**
 * Truncates text to a specified length while preserving word boundaries
 * 
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length of the text
 * @param {string} suffix - Suffix to add when text is truncated (default: '...')
 * @returns {string} - The truncated text
 */
export const truncateText = (text, maxLength = 200, suffix = '...') => {
  if (!text || typeof text !== 'string' || text.length <= maxLength) {
    return text;
  }

  const truncated = text.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  
  // If we can find a space to break on, use it to avoid cutting words
  if (lastSpaceIndex > maxLength * 0.8) {
    return truncated.substring(0, lastSpaceIndex) + suffix;
  }
  
  return truncated + suffix;
};

/**
 * Formats article title and description for display
 * 
 * @param {Object} article - The article object
 * @returns {Object} - The article with formatted title and description
 */
export const formatArticleText = (article) => {
  if (!article) return article;

  return {
    ...article,
    title: cleanText(article.title),
    description: cleanText(article.description),
  };
};
